import { memo, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Icon,
  Modal,
  ProgressBar,
  SpaceBetween,
} from '@amzn/awsui-components-core';

import { useQualityCheckModalViewModel } from './QualityCheckModalViewModel';
import styles from './QualityCheckModal.module.scss';

let nextModalRootSeq = 0;

// Cloudscape Modal always renders a close (×) button and provides no prop to hide
// it. We can't allow it here: dismissing mid-scan would skip the workflow event we
// rely on to clean up. So we render the modal into our own portal root whose
// className scopes a CSS rule that hides the dismiss control (see
// QualityCheckModal.module.scss). A per-instance id keeps two coexisting modals
// (StrictMode dev double-mount, route transition) from sharing one root and
// tearing it out from each other.
const QualityCheckModal = memo(function QualityCheckModal() {
  const vm = useQualityCheckModalViewModel();

  const modalRootIdRef = useRef<string | undefined>(undefined);
  if (!modalRootIdRef.current) {
    modalRootIdRef.current = `quality-check-modal-root-${nextModalRootSeq++}`;
  }

  const getModalRoot = useCallback(async () => {
    const id = modalRootIdRef.current!;
    const className = styles.modalRoot!;

    // Always (re)apply the class — an HMR-stale node may exist without it.
    const existing = document.getElementById(id);
    if (existing) {
      existing.className = className;
      return existing;
    }

    const created = document.createElement('div');
    created.id = id;
    created.className = className;
    document.body.appendChild(created);
    return created;
  }, []);

  const removeModalRoot = useCallback((modalRoot?: Element | null) => {
    if (modalRoot instanceof HTMLElement && modalRoot.id === modalRootIdRef.current) {
      modalRoot.remove();
    }
  }, []);

  return (
    // No onDismiss — user must explicitly cancel via the button so we send
    // a workflow event. They should not leave this modal while a scan is in progress.
    <Modal
      visible={vm.isVisible}
      getModalRoot={getModalRoot}
      removeModalRoot={removeModalRoot}
      header="Quality check in progress"
      footer={
        <Box float="right">
          <Button
            variant="link"
            onClick={vm.cancel}
            loading={vm.isCancelling}
            disabled={vm.isCancelling || !vm.canCancel}
            disabledReason={vm.canCancel ? undefined : 'You can cancel once the scan has started.'}
          >
            Cancel
          </Button>
        </Box>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        {vm.error && (
          <Alert type="error" action={<Button onClick={vm.cancel}>Retry</Button>}>
            {vm.error}
          </Alert>
        )}

        <Box color="text-body-secondary">
          AI-powered scans are analyzing your content and will provide suggestions related to
          accuracy, completeness, or adherence to best practices. This might take a few minutes.
        </Box>

        <ProgressBar
          value={vm.progress}
          status={vm.error ? 'error' : 'in-progress'}
          additionalInfo={vm.error ?? 'Initiating quality check...'}
        />

        <div className={styles.tip}>
          <Icon url="/images/lightbulb.png" variant="normal" size="large" />
          <div>
            <Box fontWeight="bold">Did you know?</Box>
            <Box color="text-body-secondary">
              This automated quality check replaces a manual peer review, which makes the review
              more consistent and significantly faster.
            </Box>
          </div>
        </div>

        <Alert type="info">
          This feature uses generative AI. Results may be inaccurate or incomplete and should be
          verified against authoritative sources.
        </Alert>
      </SpaceBetween>
    </Modal>
  );
});

export default QualityCheckModal;
